/**
 * @file: 商品拖拽
 * @author: xiaolong.huang@dmall.com
 */

import * as Toolkit from '../../../toolkit/index';
import * as UIToolkit from '../../../UIToolkit/index';
import * as CubeTree from '../../../cubeTree/index';
import FrontCubeData from '../../../frontCubeData/index';
import Utils from '../../../utils/util';
import CodiSystem from '../../../codiSystem/index';
import InsertArrow from './arrow';
import Move from '../../move/index';
import Remove from '../../remove/index';
import Select from '../../select/index';
import History from '../../history/index';
import DragRunerBase from '../dragRunerBae/index';
import { makeMoveTask } from './move';
import { performRemoveSubTask } from './removeSub';
import { findNextLayerCube } from '../funcs/findNextLayerCube';

class GoodsDragRuner extends DragRunerBase {
    private arrow: GoodsInsertArrow;
    private targetLayerChild: CubeId|null;
    private shelfsCoordinate: CubePaintCoordinate[];
    private layersInStageCoordinate: CubePaintCoordinate[];
    private topStart: number;
    private leftStart: number;
    private dragCubes: CubeId[];
    private dragLayerCubes: CubeId[];
    private dragShelfCubes: CubeId[];
    private dragPartitionCubes: CubeId[];
    private dragCube: CubeId;
    private moveTaskQueue: MoveTask;
    public dragSubGoods: boolean;
    public dragSubGoodsInfo: FreeObjMap|null;
    public dragSubGoodsIndex: number;

    constructor() {
        super();
        this.arrow = new InsertArrow();
        this.shelfsCoordinate = [];
        this.topStart = 0;
        this.leftStart = 0;
        this.dragLayerCubes = [];
        this.dragShelfCubes = [];
        this.dragPartitionCubes = [];
        this.dragCubes = [];
        this.dragSubGoods = false;
        this.dragSubGoodsInfo = null;
        this.dragSubGoodsIndex = 1;
    }

    dragStart(transfer: Transfer, dragCube: CubeId): void {
        let $sketchpad: JQuery.Coordinates = $('.shelvesbuild-e-sketchpad').offset();
        let stageCanDropLayers: CubeId[] = CubeTree.findLayersCanDropInStage();

        this.dragLayerCubes = transfer.findDragLayerCubes();
        this.dragShelfCubes = transfer.findDragShelf();
        this.dragPartitionCubes = transfer.findDragLayers();
        this.dragCubes = this.dragLayerCubes.concat(this.dragShelfCubes).concat(this.dragPartitionCubes);
        this.dragSubGoods = this.isDragSubGoods(transfer);
        this.dragSubGoodsInfo = this.getDragSubGoodsInfo(transfer);
        this.shelfsCoordinate = CodiSystem.getPaintCoordinateByType('shelf');
        this.layersInStageCoordinate = CodiSystem.getPaintCoordinates(stageCanDropLayers);
        this.leftStart = $sketchpad.left;
        this.topStart = $sketchpad.top;
        this.dragCube = dragCube;
        this.hideDragNode(transfer);
    }

    drag(params: DragParams): void {
        let mousePoint: MousePoint = {
            left: Utils.math(params.ev.clientX, this.leftStart, '-'),
            top: Utils.math(params.ev.clientY, this.topStart, '-')
        };

        if (!this.dragLayerCubes.length) {
            return;
        }

        this.arrow.move({
            mousePoint,
            shelfsCoordinate: this.shelfsCoordinate,
            layersInStageCoordinate: this.layersInStageCoordinate,
            dragCubes: this.dragLayerCubes,
            transfer: params.transfer
        });
    }

    dragEnd(params: DragEndParams): void {
        if (!this.dragLayerCubes.length) {
            return;
        }

        // 构建移动任务队列
        this.moveTaskQueue = makeMoveTask(this.arrow, this.dragLayerCubes, params.transfer);

        /** 日志打印开始 **/
        Toolkit.log({
            action: '层板放置组件拖拽',
            msgList: [
                {
                    name: '逻辑移动任务队列',
                    value: this.moveTaskQueue.logic
                },
                {
                    name: '物理移动任务队列',
                    value: this.moveTaskQueue.physics
                }
            ]
        });
        /** 日志打印结束 **/
        let isAddSubGoodsMove: boolean = this.isAddSubGoodsMoveTask(this.moveTaskQueue);

        if (isAddSubGoodsMove) {
            this.addSub();
            return;
        }

        if (this.dragSubGoods) {
            this.removeSub();
            return;
        }

        this.normalMove();
    }

    // 移除字码, 缺少撤销操作
    private async removeSub(): Promise<void> {
        let result: any = await performRemoveSubTask(this.moveTaskQueue, this.dragSubGoodsInfo, this.dragSubGoodsIndex);

        this.showDragNode();
        this.arrow.destroy();

        if (result === 'fail') {
            return;
        }

        Move.logic(this.moveTaskQueue.logic);
        Move.physics(this.moveTaskQueue.physics);
        this.moveUpadte(this.moveTaskQueue);
    }

    // 添加字母码
    private addSub(): void {
        let moveTask: LogicMoveTask = this.moveTaskQueue.logic[0];
        let targetCube: CubeNode = CubeTree.findByCubeId(moveTask.to.parent);
        let addCube: CubeNode = CubeTree.findByCubeId(moveTask.from.moveTarget);
        let stageWindow: StageWindow = window;
        let result: FreeObjMap = stageWindow.FILTER_OF_STAGECORE.getSubSkuInfo(targetCube, [addCube]);

        this.showDragNode();
        this.arrow.destroy();

        if (result.code !== '0000') {
            UIToolkit.tips('子码添加失败');
            return;
        }

        History.stackIn({
            type: 'addSub',
            data: [{
                oldParent: CubeTree.findParentIdByCubeId(moveTask.from.moveTarget),
                addCube: moveTask.from.moveTarget,
                before: findNextLayerCube(moveTask.from.moveTarget, []),
                subIndex: CubeTree.subGoodsCount(moveTask.to.parent) - 1,
                to: moveTask.to.parent
            }]
        });
        Select.clear();
        Select.panelSelectAsync();
        Remove.del([moveTask.from.moveTarget]);
        this.addSubUpdate(this.moveTaskQueue);
        UIToolkit.tips('子码添加成功');
    }

    private normalMove(): void {
        History.stackIn({
            type: 'goodsDragMove',
            data: [Move.makeBackMoveTask(this.moveTaskQueue)]
        });

        Move.logic(this.moveTaskQueue.logic);
        Move.physics(this.moveTaskQueue.physics);
        this.moveUpadte(this.moveTaskQueue);
        this.showDragNode();
        this.arrow.destroy();
    }

    private hideDragNode(transfer: Transfer): void {
        // 判断是否拖的是字码商品
        if (this.dragSubGoods) {
            this.hideSubGoods(transfer);
        }
        this.dragCubes.forEach((cubeId: CubeId): void => {
            let cube: CubeNode = CubeTree.findByCubeId(cubeId);

            cube.jView.addClass('drag-hide');
        });
    }

    private hideSubGoods(transfer: Transfer): void {
        let cube: CubeNode = CubeTree.findByCubeId(this.dragCubes[0]);

        transfer.$dragTransfer.find('.hasSubList').remove();
        transfer.$dragTransfer.find('.sub-good-item').addClass('subdrag-hide');
        transfer.$dragTransfer.find('.main-goods').addClass('subdrag-hide');
        transfer.$dragTransfer.find(`.sub-good-item[data-subindex="${this.dragSubGoodsIndex}"]`).removeClass('subdrag-hide');
    }

    private showDragNode(): void {
        this.dragCubes.forEach((cubeId: CubeId): void => {
            let cube: CubeNode = CubeTree.findByCubeId(cubeId);

            cube.jView.removeClass('drag-hide');
        });
    }

    private getDragSubGoodsInfo(transfer: Transfer): FreeObjMap|null {
        let result: FreeObjMap|null = null;

        if (!this.dragSubGoods) {
            return null;
        }

        let sub: JQuery<HTMLElement> = transfer.$target.closest('.sub-good-item');

        if (!sub.length) {
            return;
        }

        this.dragSubGoodsIndex = Utils.toNumberForce(sub.attr('data-subindex'));
        let subList: SkuData[] = FrontCubeData.getValue(this.dragCubes[0], 'subSkuList');

        result = subList[this.dragSubGoodsIndex + 1] || null;
        return result;
    }

    private isDragSubGoods(transfer: Transfer): boolean {
        if (this.dragCubes.length > 1) {
            return false;
        }

        let layer: CubeId = CubeTree.findParentIdByCubeId(this.dragCubes[0]);
        let isSubDragLayer: boolean = CubeTree.layerIsSubDrag(layer);

        if (!isSubDragLayer) {
            return false;
        }

        if (!CubeTree.isGoods(this.dragCubes[0])) {
            return false;
        }

        if (!CubeTree.hasSubGoods(this.dragCubes[0])) {
            return false;
        }

        let sub: JQuery<HTMLElement> = transfer.$target.closest('.sub-good-item');

        if (!sub.length) {
            return false;
        }

        return true;
    }
}

export default GoodsDragRuner;
